/**
 * API端点统一配置文件
 * 
 * ⚠️ 重要提醒 ⚠️
 * ================================================================================
 * 所有API端点必须使用此文件中定义的常量，严禁硬编码URL路径！
 * 
 * 原因：
 * 1. 避免路径拼接错误（如遗漏 /api/v1 前缀）
 * 2. 便于统一管理和修改API路径
 * 3. 防止因路径错误导致404问题
 * 
 * 使用方法：
 * ```typescript
 * import { API_ENDPOINTS } from '@/api/endpoints'
 * 
 * // ✅ 正确
 * await apiClient.get(API_ENDPOINTS.USERS.LIST)
 * await apiClient.get(API_ENDPOINTS.USERS.DETAIL(userId))
 * 
 * // ❌ 错误 - 不要硬编码路径
 * await apiClient.get('/admin/users')  // 缺少 /api/v1 前缀
 * await apiClient.get('/api/v1/admin/users')  // 硬编码不利于维护
 * ```
 * ================================================================================
 */

// API基础前缀 - 所有API都必须使用这个前缀
const API_PREFIX = '/api/v1'

// 管理端API前缀
const ADMIN_PREFIX = `${API_PREFIX}/admin`

/**
 * API端点配置对象
 * 按功能模块组织，每个模块包含相关的所有端点
 */
export const API_ENDPOINTS = {
  // ========== 认证相关 ==========
  AUTH: {
    LOGIN: `${ADMIN_PREFIX}/auth/login`,
    LOGOUT: `${ADMIN_PREFIX}/auth/logout`,
    ME: `${ADMIN_PREFIX}/auth/me`,
    REFRESH_TOKEN: `${ADMIN_PREFIX}/auth/refresh-token`,
    CHANGE_PASSWORD: `${ADMIN_PREFIX}/auth/change-password`
  },

  // ========== 用户管理 ==========
  USERS: {
    LIST: `${ADMIN_PREFIX}/users`,
    DETAIL: (id: number) => `${ADMIN_PREFIX}/users/${id}`,
    CREATE: `${ADMIN_PREFIX}/users`,
    UPDATE: (id: number) => `${ADMIN_PREFIX}/users/${id}`,
    DELETE: (id: number) => `${ADMIN_PREFIX}/users/${id}`,
    TOGGLE_STATUS: (id: number) => `${ADMIN_PREFIX}/users/${id}/toggle-status`,
    RESET_PASSWORD: (id: number) => `${ADMIN_PREFIX}/users/${id}/reset-password`,
    STATS: (id: number) => `${ADMIN_PREFIX}/users/${id}/stats`,
    LLM_LOGS: (id: number) => `${ADMIN_PREFIX}/users/${id}/llm-logs`,
    ALL_LLM_LOGS: `${ADMIN_PREFIX}/llm-request-logs`
  },

  // ========== 内容管理 ==========
  CONTENT: {
    LIST: `${ADMIN_PREFIX}/content`,
    DETAIL: (id: number) => `${ADMIN_PREFIX}/content/${id}`,
    CREATE: `${ADMIN_PREFIX}/content`,
    UPDATE: (id: number) => `${ADMIN_PREFIX}/content/${id}`,
    DELETE: (id: number) => `${ADMIN_PREFIX}/content/${id}`,
    BATCH_DELETE: `${ADMIN_PREFIX}/content/batch-delete`
  },

  // ========== 主题管理 ==========
  THEMES: {
    LIST: `${ADMIN_PREFIX}/themes`,
    DETAIL: (id: number) => `${ADMIN_PREFIX}/themes/${id}`,
    CREATE: `${ADMIN_PREFIX}/themes`,
    UPDATE: (id: number) => `${ADMIN_PREFIX}/themes/${id}`,
    DELETE: (id: number) => `${ADMIN_PREFIX}/themes/${id}`,
    STATS: (id: number) => `${ADMIN_PREFIX}/themes/${id}/stats`,
    UPLOAD_COVER: `${ADMIN_PREFIX}/themes/uploads/cover`,
    UPLOAD_CHARACTER: `${ADMIN_PREFIX}/themes/uploads/character`
  },

  // ========== 统计分析 ==========
  STATS: {
    OVERVIEW: `${ADMIN_PREFIX}/stats/overview`,
    DAILY: `${ADMIN_PREFIX}/stats/daily`,
    USER_GROWTH: `${ADMIN_PREFIX}/stats/user-growth`,
    CONTENT_STATS: `${ADMIN_PREFIX}/stats/content`,
    TOKEN_USAGE: `${ADMIN_PREFIX}/stats/token-usage`,
    ACTIVE_USERS: `${ADMIN_PREFIX}/stats/active-users`,
    SYSTEM_OVERVIEW: `${ADMIN_PREFIX}/stats/system`,
    TRENDS: `${ADMIN_PREFIX}/stats/trends`,
    DISTRIBUTION: `${ADMIN_PREFIX}/stats/distribution`,
    REALTIME: `${ADMIN_PREFIX}/stats/realtime`
  },

  // ========== 系统管理 ==========
  SYSTEM: {
    SETTINGS: `${ADMIN_PREFIX}/system/settings`,
    UPDATE_SETTINGS: `${ADMIN_PREFIX}/system/settings`,
    LOGS: `${ADMIN_PREFIX}/system/logs`,
    CACHE: {
      STATUS: `${ADMIN_PREFIX}/system/cache/status`,
      CLEAR: `${ADMIN_PREFIX}/system/cache/clear`
    },
    BACKUP: {
      CREATE: `${ADMIN_PREFIX}/system/backup`,
      LIST: `${ADMIN_PREFIX}/system/backups`,
      RESTORE: (id: string) => `${ADMIN_PREFIX}/system/backup/${id}/restore`
    }
  },

  // ========== 词库管理 ==========
  LEXICON: {
    LIST: `${ADMIN_PREFIX}/lexicon`,
    DETAIL: (id: number) => `${ADMIN_PREFIX}/lexicon/${id}`,
    IMPORT: `${ADMIN_PREFIX}/lexicon/import`,
    DELETE: (id: number) => `${ADMIN_PREFIX}/lexicon/${id}`,
    BATCH_DELETE: `${ADMIN_PREFIX}/lexicon/batch-delete`,
    STATS: `${ADMIN_PREFIX}/lexicon/stats`
  },

  // ========== AI服务 ==========
  AI: {
    PROVIDERS: `${ADMIN_PREFIX}/ai/providers`,
    PROVIDER_STATUS: (provider: string) => `${ADMIN_PREFIX}/ai/providers/${provider}/status`,
    TOKEN_STATS: `${ADMIN_PREFIX}/ai/token-stats`,
    USAGE_HISTORY: `${ADMIN_PREFIX}/ai/usage-history`
  },

  // ========== AI供应商管理（兼容旧代码） ==========
  aiProviders: {
    list: `${ADMIN_PREFIX}/ai-providers`,
    detail: (id: number) => `${ADMIN_PREFIX}/ai-providers/${id}`,
    create: `${ADMIN_PREFIX}/ai-providers`,
    update: (id: number) => `${ADMIN_PREFIX}/ai-providers/${id}`,
    delete: (id: number) => `${ADMIN_PREFIX}/ai-providers/${id}`,
    test: (id: number) => `${ADMIN_PREFIX}/ai-providers/${id}/test`,
    testStream: (id: number) => `${ADMIN_PREFIX}/ai-providers/${id}/test-stream`,
    fetchPrice: `${ADMIN_PREFIX}/ai-providers/fetch-price`
  },

  // ========== Token使用统计（兼容旧代码） ==========
  tokenUsage: {
    userStats: `${ADMIN_PREFIX}/token-usage/user`,
    globalStats: `${ADMIN_PREFIX}/token-usage/global`,
    providerStats: `${ADMIN_PREFIX}/token-usage/provider-stats`,
    dailyTrend: `${ADMIN_PREFIX}/token-usage/daily-trend`,
    modelRanking: `${ADMIN_PREFIX}/token-usage/model-ranking`,
    userRanking: `${ADMIN_PREFIX}/token-usage/user-ranking`,
    usageDetails: `${ADMIN_PREFIX}/token-usage/usage-details`
  },

  // ========== 审计日志 ==========
  AUDIT_LOGS: {
    LIST: `${ADMIN_PREFIX}/audit-logs`,
    ACTIONS: `${ADMIN_PREFIX}/audit-logs/actions`,
    TARGET_TYPES: `${ADMIN_PREFIX}/audit-logs/target-types`
  },

  // ========== 管理员管理 ==========
  ADMIN_MANAGEMENT: {
    LIST: `${ADMIN_PREFIX}/admins`,
    DETAIL: (id: number) => `${ADMIN_PREFIX}/admins/${id}`,
    CREATE: `${ADMIN_PREFIX}/admins`,
    UPDATE: (id: number) => `${ADMIN_PREFIX}/admins/${id}`,
    DELETE: (id: number) => `${ADMIN_PREFIX}/admins/${id}`,
    UPDATE_ROLE: (id: number) => `${ADMIN_PREFIX}/admins/${id}/role`,
    UPDATE_PERMISSIONS: (id: number) => `${ADMIN_PREFIX}/admins/${id}/permissions`,
    UPDATE_STATUS: (id: number) => `${ADMIN_PREFIX}/admins/${id}/status`,
    RESET_PASSWORD: (id: number) => `${ADMIN_PREFIX}/admins/${id}/reset-password`,
    UPDATE_PROFILE: `${ADMIN_PREFIX}/admins/profile`
  },

  // ========== 管理概览（兼容旧代码） ==========
  admin: {
    overview: `${ADMIN_PREFIX}/overview`,
    userVocabulary: (userId: number) => `${ADMIN_PREFIX}/users/${userId}/vocabulary`,
    wordEvidence: (userId: number, lexemeId: number) => `${ADMIN_PREFIX}/users/${userId}/vocabulary/${lexemeId}/evidence`,
    userDetail: (userId: number) => `${ADMIN_PREFIX}/users/${userId}`,
    clearAllUserData: (userId: number) => `${ADMIN_PREFIX}/users/${userId}/all-data`,
  },

  // ========== 统计（兼容旧代码） ==========
  stats: {
    overview: `${ADMIN_PREFIX}/stats/overview`,
    daily: `${ADMIN_PREFIX}/stats/daily`,
    userGrowth: `${ADMIN_PREFIX}/stats/user-growth`,
    contentStats: `${ADMIN_PREFIX}/stats/content`,
    tokenUsage: `${ADMIN_PREFIX}/stats/token-usage`,
    activeUsers: `${ADMIN_PREFIX}/stats/active-users`,
    users: `${ADMIN_PREFIX}/stats/active-users`,
    content: `${ADMIN_PREFIX}/stats/content`,
    system: `${ADMIN_PREFIX}/stats/system`,
    trends: `${ADMIN_PREFIX}/stats/trends`,
    distribution: `${ADMIN_PREFIX}/stats/distribution`,
    realtime: `${ADMIN_PREFIX}/stats/realtime`
  },

  // ========== 用户主题实例管理 ==========
  USER_INSTANCES: {
    LIST: `${ADMIN_PREFIX}/user-instances`,
    DETAIL: (id: number) => `${ADMIN_PREFIX}/user-instances/${id}`,
    CONTENT: (id: number) => `${ADMIN_PREFIX}/user-instances/${id}/content`,
    STATS: (id: number) => `${ADMIN_PREFIX}/user-instances/${id}/stats`,
    PAUSE: (id: number) => `${ADMIN_PREFIX}/user-instances/${id}/pause`,
    RESUME: (id: number) => `${ADMIN_PREFIX}/user-instances/${id}/resume`,
    COMPLETE: (id: number) => `${ADMIN_PREFIX}/user-instances/${id}/complete`,
    EPISODES: (id: number) => `${ADMIN_PREFIX}/user-instances/${id}/episodes`,
    EPISODE: (id: number, episodeId: number) => `${ADMIN_PREFIX}/user-instances/${id}/episodes/${episodeId}`,
    EPISODE_CONTENT: (id: number, episodeId: number) =>
      `${ADMIN_PREFIX}/user-instances/${id}/episodes/${episodeId}/content`
  },

  // ========== 产品 KPI 指标看板 ==========
  METRICS: {
    // 列出所有受支持指标 + 自然语言定义
    DASHBOARD_LIST: `${API_PREFIX}/metrics/dashboard`,
    // 单个指标的最近 N 天序列
    DASHBOARD_SERIES: (metricName: string) => `${API_PREFIX}/metrics/dashboard/${metricName}`,
  },
} as const

// 导出类型定义，方便类型检查
export type ApiEndpoints = typeof API_ENDPOINTS

/**
 * 工具函数：构建带查询参数的URL
 * @param endpoint - API端点
 * @param params - 查询参数
 * @returns 完整的URL字符串
 * 
 * @example
 * buildUrl(API_ENDPOINTS.USERS.LIST, { page: 1, page_size: 20 })
 * // 返回: "/api/v1/admin/users?page=1&page_size=20"
 */
export function buildUrl(endpoint: string, params?: Record<string, any>): string {
  if (!params) return endpoint
  
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')
  
  return queryString ? `${endpoint}?${queryString}` : endpoint
}

/**
 * 验证函数：检查URL是否包含正确的API前缀
 * 仅用于开发环境的调试
 */
export function validateApiUrl(url: string): void {
  if (import.meta.env.DEV) {
    if (!url.startsWith(API_PREFIX) && !url.startsWith('http')) {
      console.warn(`⚠️ API URL缺少前缀: ${url}`)
      console.warn(`应该使用: ${API_PREFIX}${url}`)
      console.trace('调用堆栈')
    }
  }
}